import httpService from "./httpService";
import { FactorsResponse } from "./productFactorsService";

export interface OptionChainContract {
  conId: number;
  underlyingContractId: number;
  symbol: string;
  optionSymbol: string;
  expiryDateLocal: string;
  strikePrice: number;
  putCall: string;
  multiplier: number;
  exchange: string;
}

export interface GetOptionContractsByIdsResponse {
  exchange: string;
  optionContracts: OptionChainContract[];
  requestedCount: number;
  returnedCount: number;
}

export interface ExpirationDatesResponse {
  underlyingContractId: number;
  expirationDates: string[];
  count: number;
}

export interface StrikePriceGroup {
  expirationDate: string;
  strikePrices: string[];
}

export interface StrikePricesByExpirationResponse {
  underlyingContractId: number;
  strikePricesByExpiration: StrikePriceGroup[];
}

export interface PagedOptionChainsResponse {
  currentPage: number;
  currentPageNumOfItems: number;
  totalPages: number;
  totalItems: number;
  items: OptionChainContract[];
}

export interface OptionHistoricalPriceItem {
  datetimeUtc: string;
  conId: number;
  exchange: string;
  source: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
}

export interface OptionHistoricalPricesResponse {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: OptionHistoricalPriceItem[];
}

const BASE_PATH = "/v2/option-chains";
const HARDCODED_EXCHANGE = "CBOE";
const MAX_BATCH_SIZE = 50;

/**
 * Backend returns Prisma Decimal fields (strikePrice, multiplier) as strings.
 * Coerce to numbers at the service boundary. Invalid option multipliers stay
 * invalid as 0 so option consumers can fail closed instead of assuming coverage.
 */
function normalizeContract(raw: OptionChainContract): OptionChainContract {
  const rawMultiplier: unknown = raw.multiplier;
  const multiplier =
    typeof rawMultiplier === "number" || typeof rawMultiplier === "string"
      ? Number(rawMultiplier)
      : Number.NaN;
  return {
    ...raw,
    strikePrice: Number(raw.strikePrice) || 0,
    multiplier: Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 0,
  };
}

class OptionChainsV2Service {
  async getOptionPricesPage(
    conId: number,
    pageNumber: number,
    from?: string,
    to?: string,
    exchange: string = HARDCODED_EXCHANGE
  ): Promise<OptionHistoricalPricesResponse> {
    const params = new URLSearchParams();
    params.set("conId", String(conId));
    params.set("exchange", exchange);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("pageNumber", String(pageNumber));

    const response = await httpService.get<OptionHistoricalPricesResponse>(
      `${BASE_PATH}/prices?${params.toString()}`
    );
    return response.data;
  }

  async getOptionFactorsPage(
    conId: number,
    pageNumber: number,
    from?: string,
    to?: string
  ): Promise<FactorsResponse> {
    const params = new URLSearchParams();
    params.set("conId", String(conId));
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("pageNumber", String(pageNumber));

    const response = await httpService.get<FactorsResponse>(
      `${BASE_PATH}/factors?${params.toString()}`
    );
    return response.data;
  }

  async findOptionContractsByConIds(
    conIds: number[]
  ): Promise<GetOptionContractsByIdsResponse> {
    if (conIds.length === 0) {
      return {
        exchange: HARDCODED_EXCHANGE,
        optionContracts: [],
        requestedCount: 0,
        returnedCount: 0,
      };
    }

    if (conIds.length <= MAX_BATCH_SIZE) {
      const response = await httpService.post<GetOptionContractsByIdsResponse>(
        `${BASE_PATH}/by-con-ids`,
        { conIds, exchange: HARDCODED_EXCHANGE }
      );
      response.data.optionContracts = response.data.optionContracts.map(normalizeContract);
      return response.data;
    }

    const allContracts: OptionChainContract[] = [];
    for (let i = 0; i < conIds.length; i += MAX_BATCH_SIZE) {
      const chunk = conIds.slice(i, i + MAX_BATCH_SIZE);
      const response = await httpService.post<GetOptionContractsByIdsResponse>(
        `${BASE_PATH}/by-con-ids`,
        { conIds: chunk, exchange: HARDCODED_EXCHANGE }
      );
      allContracts.push(...response.data.optionContracts.map(normalizeContract));
    }

    return {
      exchange: HARDCODED_EXCHANGE,
      optionContracts: allContracts,
      requestedCount: conIds.length,
      returnedCount: allContracts.length,
    };
  }

  async getExpirationDates(
    underlyingContractId: number
  ): Promise<ExpirationDatesResponse> {
    const response = await httpService.get<ExpirationDatesResponse>(
      `${BASE_PATH}/expiration-dates/${underlyingContractId}`
    );
    return response.data;
  }

  async getStrikePrices(
    underlyingContractId: number,
    expirationDates: string[]
  ): Promise<StrikePricesByExpirationResponse> {
    const params = new URLSearchParams();
    params.set("expirationDates", expirationDates.join(","));
    const response = await httpService.get<StrikePricesByExpirationResponse>(
      `${BASE_PATH}/strike-prices/${underlyingContractId}?${params.toString()}`
    );
    return response.data;
  }

  async searchOptionChains(params: {
    underlyingContractId: number;
    expirationDates?: string;
    strikePrices?: string;
    pageSize?: number;
    pageNumber?: number;
  }): Promise<PagedOptionChainsResponse> {
    const qp = new URLSearchParams();
    qp.set("underlyingContractId", String(params.underlyingContractId));
    if (params.expirationDates) qp.set("expirationDates", params.expirationDates);
    if (params.strikePrices) qp.set("strikePrices", params.strikePrices);
    if (params.pageSize) qp.set("pageSize", String(params.pageSize));
    if (params.pageNumber) qp.set("pageNumber", String(params.pageNumber));
    const response = await httpService.get<PagedOptionChainsResponse>(
      `${BASE_PATH}?${qp.toString()}`
    );
    response.data.items = response.data.items.map(normalizeContract);
    return response.data;
  }
}

export const optionChainsV2Service = new OptionChainsV2Service();
export default optionChainsV2Service;
